/** Column-major 4x4 matrices and the camera that ties the photo horizon to the world. */

export type Mat4 = Float32Array;

export function perspective(fovy: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovy / 2);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = (far + near) / (near - far);
  m[11] = -1;
  m[14] = (2 * far * near) / (near - far);
  return m;
}

export interface Camera {
  proj: Mat4;
  view: Mat4;
  fovy: number;
  aspect: number;
  /** Positive looks up. */
  pitch: number;
  pos: [number, number, number];
  /** Pixels per metre at 1 m depth, in device pixels. */
  pxPerMeterAt1: number;
}

export const FOVY = (40 * Math.PI) / 180;
const CAM_HEIGHT = 2;

/**
 * horizonScreen is the horizon's position on screen, 0 = top, 1 = bottom.
 * The camera is pitched so that the world horizon lands there.
 */
export function makeCamera(viewW: number, viewH: number, horizonScreen: number): Camera {
  const aspect = viewW / Math.max(1, viewH);
  const tanHalf = Math.tan(FOVY / 2);
  const ndcY = 1 - 2 * horizonScreen;
  const pitch = Math.atan(-ndcY * tanHalf);
  const view = new Float32Array(16);
  const c = Math.cos(-pitch);
  const s = Math.sin(-pitch);
  view[0] = 1;
  view[5] = c;
  view[6] = s;
  view[9] = -s;
  view[10] = c;
  view[15] = 1;
  // translation: Rx(-pitch) * (0, -CAM_HEIGHT, 0)
  view[13] = c * -CAM_HEIGHT;
  view[14] = s * -CAM_HEIGHT;
  return {
    proj: perspective(FOVY, aspect, 0.5, 4000),
    view,
    fovy: FOVY,
    aspect,
    pitch,
    pos: [0, CAM_HEIGHT, 0],
    pxPerMeterAt1: viewH / 2 / tanHalf,
  };
}

/** World direction of the ray through a screen point (fractions, y down). */
export function rayDir(cam: Camera, sx: number, sy: number): [number, number, number] {
  const tanHalf = Math.tan(cam.fovy / 2);
  const nx = sx * 2 - 1;
  const ny = 1 - sy * 2;
  const dv: [number, number, number] = [nx * tanHalf * cam.aspect, ny * tanHalf, -1];
  const c = Math.cos(cam.pitch);
  const s = Math.sin(cam.pitch);
  const y = c * dv[1] - s * dv[2];
  const z = s * dv[1] + c * dv[2];
  const len = Math.hypot(dv[0], y, z);
  return [dv[0] / len, y / len, z / len];
}

/** Unit vector the camera looks along, in world space. */
export function cameraForward(cam: Camera): [number, number, number] {
  return [0, Math.sin(cam.pitch), -Math.cos(cam.pitch)];
}

/**
 * World point under a screen position (fractions, y down), on the plane
 * through `anchor` that faces the camera. That plane is where the flock
 * lives, so a finger lands among the birds rather than in front of or
 * behind them.
 */
export function pointerToWorld(cam: Camera, sx: number, sy: number, anchor: [number, number, number]): [number, number, number] {
  const d = rayDir(cam, sx, sy);
  const n = cameraForward(cam);
  const denom = d[0] * n[0] + d[1] * n[1] + d[2] * n[2];
  const num = (anchor[0] - cam.pos[0]) * n[0] + (anchor[1] - cam.pos[1]) * n[1] + (anchor[2] - cam.pos[2]) * n[2];
  const t = Math.max(5, num / Math.max(1e-4, denom));
  return [cam.pos[0] + d[0] * t, Math.max(3, cam.pos[1] + d[1] * t), cam.pos[2] + d[2] * t];
}

/**
 * Direction from the scene toward the light, world space. Azimuth in degrees
 * in the camera frame: 0 ahead of the viewer (backlit birds), 90 right,
 * 180 behind the viewer, 270 left. A warm light sits low; a cool one higher.
 */
export function lightDirection(angleDeg: number, warmth: number): [number, number, number] {
  const az = (angleDeg * Math.PI) / 180;
  const elev = ((35 - 27 * warmth) * Math.PI) / 180;
  const c = Math.cos(elev);
  return [Math.sin(az) * c, Math.sin(elev), -Math.cos(az) * c];
}

/**
 * Where the flock lives: on the ray through the screen point above the horizon
 * chosen by flockHeight, at the horizontal distance chosen by flockDistance.
 */
export function flockAnchor(cam: Camera, horizonScreen: number, flockHeight: number, flockDistance: number): [number, number, number] {
  const sy = horizonScreen * (1 - 0.1 - 0.8 * flockHeight);
  const d = rayDir(cam, 0.5, sy);
  const dist = 100 * Math.pow(8, flockDistance);
  const horiz = Math.hypot(d[0], d[2]);
  const t = dist / Math.max(horiz, 0.05);
  const y = Math.max(8, cam.pos[1] + d[1] * t);
  return [cam.pos[0] + d[0] * t, y, cam.pos[2] + d[2] * t];
}
